import moment from 'moment';
import { createIntervalDescription } from '../tasks/helpers';
import { LastFourHoursTasks } from '../tasks/components/HourlyChecklist';
import {
  ListTaskOccurrencesParams,
  TaskTemplate,
  TaskOccurrence,
} from '../../core/api/rtk/taskTemplatesApi';

// Map the TaskTemplate interval *string* (as the API returns it) back to the
// same integer the legacy Task model uses. Lets us share the legacy
// createIntervalDescription helper (and its locale keys) without duplicating
// every "Every 2 Days" / "Weekly (Mon, Wed)" template.
const INTERVAL_STRING_TO_INT: Record<string, number> = {
  CarCount: 0,
  Once: 1,
  Daily: 2,
  Weekly: 3,
  Monthly: 4,
  Yearly: 5,
  Hourly: 6,
};

interface IntlLike {
  formatMessage: (descriptor: { id: string }, values?: Record<string, any>) => string;
}

export const getTaskTemplateIntervalDescription = (task: TaskTemplate, intl: IntlLike): string => {
  const intervalInt = INTERVAL_STRING_TO_INT[task.interval];
  if (intervalInt === undefined) return task.interval || '';
  return createIntervalDescription(
    {
      interval: intervalInt,
      intervalCount: task.intervalCount ?? 1,
      weeklySchedule: task.weeklySchedule ?? null,
      monthlySameWeekday: false,
      start: task.start,
      intervalCarCount: task.intervalCarCount ?? null,
      startingCarCount: task.startingCarCount ?? null,
    },
    intl.formatMessage
  );
};

interface EmployeeLike {
  id: number;
  firstName?: string;
  lastName?: string;
  name?: string;
}

// Mirrors getLastCompleted from views/tasks/helpers but reads from the
// TaskTemplate latestOccurrence shape (completedAt + completedById) instead of
// the legacy TaskCompletion shape (timestamp + employeeId).
export const getTaskTemplateLastCompleted = (
  occurrence: TaskOccurrence | null | undefined,
  employees: EmployeeLike[]
): string => {
  if (!occurrence || !occurrence.completedAt) return '';
  const when = moment(occurrence.completedAt).format('MM/DD/YYYY h:mm A');
  const empId = occurrence.completedById;
  if (!empId) return when;
  const emp = (employees || []).find((e) => Number(e.id) === Number(empId));
  if (!emp) return when;
  const empLabel = emp.name || `${emp.firstName ?? ''} ${emp.lastName ?? ''}`.trim() || `#${empId}`;
  return `${when} (${empLabel})`;
};

// ---------------------------------------------------------------------------
// Occurrence due/done helpers — parallel to maintenanceTasks/helpers but bound
// to the v2 TaskOccurrence shape (no forecast / CarCount data on v2 yet,
// so we fall back through scheduleEnd → scheduleStart → task.end → task.start
// without the forecast layer the legacy completion has).
// ---------------------------------------------------------------------------

const pickDueDateString = (occurrence: TaskOccurrence): string | null | undefined => {
  return (
    occurrence.scheduleEnd ||
    occurrence.scheduleStart ||
    (occurrence.task as any)?.end ||
    (occurrence.task as any)?.start
  );
};

export const getOccurrenceDueDate = (occurrence: TaskOccurrence): Date => {
  const due = pickDueDateString(occurrence);
  return due ? new Date(due) : new Date();
};

export const getOccurrenceDueMoment = (occurrence: TaskOccurrence): moment.Moment | null => {
  const due = pickDueDateString(occurrence);
  return due ? moment(due) : null;
};

export const getOccurrenceDoneMoment = (occurrence: TaskOccurrence): moment.Moment | null => {
  if (occurrence.status !== 'Completed') return null;
  const stamp = occurrence.completedAt || occurrence.timestamp;
  return stamp ? moment(stamp) : null;
};

export const isOccurrenceOverdue = (occurrence: TaskOccurrence): boolean => {
  if (!occurrence || occurrence.status === 'Completed') return false;
  const due = occurrence.scheduleEnd || occurrence.scheduleStart;
  if (!due) return false;
  return moment(due).isBefore(moment());
};

// Mirror of buildMaintenanceQuery from views/maintenanceTasks/helpers.tsx:
// scopes the query to type=Maintenance and (by default) the past 30 days
// through today so we get both overdue rows and the active checklist in one
// fetch. Caller can override fromDate / untilDate to widen for the calendar.
const DEFAULT_MAINTENANCE_BACK_DAYS = 30;

export const buildMaintenanceOccurrenceQuery = (
  overrides: Partial<ListTaskOccurrencesParams> = {}
): ListTaskOccurrencesParams => {
  const fromDate =
    overrides.fromDate ??
    moment().subtract(DEFAULT_MAINTENANCE_BACK_DAYS, 'days').format('YYYY-MM-DD');
  return {
    type: 'Maintenance',
    pageSize: 500,
    fromDate,
    ...overrides,
  };
};

// Reconstructs the legacy `lastFourHoursTasks` aggregation client-side from a
// flat list of hourly occurrences. The legacy backend returned this shape
// keyed by hour-of-day; HourlyChecklist iterates the keys with `.sort()` and
// treats index 3 as the current hour, so we emit zero-padded keys and feed
// them in chronological order (oldest -> current).
const LAST_FOUR_HOURS_COUNT = 4;
const MS_PER_HOUR = 60 * 60 * 1000;

export const buildLastFourHoursOccurrences = (
  hourlyOccurrences: TaskOccurrence[],
  now: Date = new Date()
): LastFourHoursTasks => {
  const result: LastFourHoursTasks = {};
  const todayStart = moment(now).startOf('day');
  for (let offset = LAST_FOUR_HOURS_COUNT - 1; offset >= 0; offset -= 1) {
    const hourMoment = moment(now).subtract(offset, 'hours');
    const hour = hourMoment.hour();
    const key = String(hour).padStart(2, '0');
    const matching = hourlyOccurrences.filter((occurrence) => {
      const scheduled = occurrence.scheduleStart || occurrence.scheduleEnd;
      if (!scheduled) return false;
      const due = moment(scheduled);
      return due.hour() === hour && due.isSame(todayStart, 'day');
    });
    const completed = matching.filter((o) => o.status === 'Completed').length;
    const total = matching.length;
    result[key] = { completed, total, summary: `${completed}/${total}` };
  }
  return result;
};
